import { isEmbedded } from '../../../base/util/embedUtils';
import { downloadBlob } from '../../../local-recording/recording/Utils';
import logger from '../../logger';

const LOCAL_RECORDING_DOWNLOAD_REQUEST = 'meethour.localRecording.download.request';
const LOCAL_RECORDING_DOWNLOAD_RESPONSE = 'meethour.localRecording.download.response';
const REQUEST_TIMEOUT_MS = 1500;

let bridgeRegistered = false;

/**
 * Gets the allowlisted origins for bridge messaging.
 *
 * @returns {string[]}
 */
function getAllowedOrigins() {
    const configured = window.interfaceConfig?.LOCAL_RECORDING_DOWNLOAD_BRIDGE_ALLOWED_ORIGINS;

    if (Array.isArray(configured) && configured.length) {
        return configured;
    }

    return [ window.location.origin ];
}

/**
 * Checks whether the provided origin is allowlisted.
 *
 * @param {string} origin - Event origin.
 * @returns {boolean}
 */
function isOriginAllowed(origin) {
    const allowedOrigins = getAllowedOrigins();

    return allowedOrigins.includes('*') || allowedOrigins.includes(origin);
}

/**
 * Computes target origin for postMessage response.
 *
 * @param {string} origin - Request origin.
 * @returns {string}
 */
function getTargetOrigin(origin) {
    return origin && origin !== 'null' ? origin : '*';
}

/**
 * Sends download bridge response to caller.
 *
 * @param {WindowProxy} targetWindow - Window to receive response.
 * @param {string} origin - Target origin.
 * @param {string} requestId - Request id.
 * @param {boolean} ok - Operation status.
 * @returns {void}
 */
function postDownloadResponse(targetWindow, origin, requestId, ok) {
    try {
        targetWindow?.postMessage({
            type: LOCAL_RECORDING_DOWNLOAD_RESPONSE,
            requestId,
            ok
        }, getTargetOrigin(origin));
    } catch (error) {
        logger.warn('Failed to send local recording download bridge response', error);
    }
}

/**
 * Registers message handler in parent/top page for local recording download requests.
 *
 * @returns {void}
 */
export function registerLocalRecordingDownloadBridge() {
    if (bridgeRegistered || typeof window === 'undefined') {
        return;
    }

    window.addEventListener('message', event => {
        const { data } = event;

        if (!data || data.type !== LOCAL_RECORDING_DOWNLOAD_REQUEST) {
            return;
        }

        const { payload = {} } = data;
        const {
            blob,
            fileName,
            probe,
            requestId
        } = payload;

        if (!requestId || !isOriginAllowed(event.origin)) {
            postDownloadResponse(event.source, event.origin, requestId, false);

            return;
        }

        if (probe) {
            postDownloadResponse(event.source, event.origin, requestId, true);

            return;
        }

        if (!(blob instanceof Blob) || !fileName) {
            postDownloadResponse(event.source, event.origin, requestId, false);

            return;
        }

        try {
            downloadBlob(blob, fileName);
            postDownloadResponse(event.source, event.origin, requestId, true);
        } catch (error) {
            logger.error('Parent download bridge failed to save local recording', error);
            postDownloadResponse(event.source, event.origin, requestId, false);
        }
    });

    bridgeRegistered = true;
}

/**
 * Requests the parent window to trigger local recording file download.
 *
 * @param {Blob} blob - Recording blob.
 * @param {string} fileName - Download file name.
 * @returns {Promise<boolean>}
 */
export function requestParentRecordingDownload(blob, fileName) {
    if (!isEmbedded() || !window.parent || window.parent === window) {
        return Promise.resolve(false);
    }

    const requestId = `${Date.now()}-${Math.random().toString(16)
.slice(2)}`;

    return new Promise(resolve => {
        let isDone = false;
        let timeoutId = 0;

        /**
         * Completes request lifecycle and cleans listeners.
         *
         * @param {boolean} result - Probe/download result.
         * @returns {void}
         */
        function complete(result) {
            if (isDone) {
                return;
            }
            isDone = true;
            window.removeEventListener('message', onMessage);
            window.clearTimeout(timeoutId);
            resolve(result);
        }

        /**
         * Handles parent response messages for download requests.
         *
         * @param {MessageEvent} event - Incoming postMessage event.
         * @returns {void}
         */
        function onMessage(event) {
            const { data } = event;

            if (event.source !== window.parent
                || !data
                || data.type !== LOCAL_RECORDING_DOWNLOAD_RESPONSE
                || data.requestId !== requestId) {
                return;
            }

            if (!isOriginAllowed(event.origin)) {
                complete(false);

                return;
            }

            complete(Boolean(data.ok));
        }

        timeoutId = window.setTimeout(() => complete(false), REQUEST_TIMEOUT_MS);

        window.addEventListener('message', onMessage);

        try {
            window.parent.postMessage({
                type: LOCAL_RECORDING_DOWNLOAD_REQUEST,
                payload: {
                    blob,
                    fileName,
                    requestId
                }
            }, '*');
        } catch (error) {
            logger.warn('Failed to request parent download bridge for local recording', error);
            complete(false);
        }
    });
}

/**
 * Probes whether parent window bridge integration is available.
 *
 * @returns {Promise<boolean>}
 */
export function isParentRecordingDownloadBridgeAvailable() {
    if (!isEmbedded() || !window.parent || window.parent === window) {
        return Promise.resolve(false);
    }

    const requestId = `${Date.now()}-${Math.random().toString(16)
.slice(2)}`;

    return new Promise(resolve => {
        let isDone = false;
        let timeoutId = 0;

        /**
         * Completes probe lifecycle and cleans listeners.
         *
         * @param {boolean} result - Probe result.
         * @returns {void}
         */
        function complete(result) {
            if (isDone) {
                return;
            }
            isDone = true;
            window.removeEventListener('message', onMessage);
            window.clearTimeout(timeoutId);
            resolve(result);
        }

        /**
         * Handles parent response messages for bridge probe requests.
         *
         * @param {MessageEvent} event - Incoming postMessage event.
         * @returns {void}
         */
        function onMessage(event) {
            const { data } = event;

            if (event.source !== window.parent
                || !data
                || data.type !== LOCAL_RECORDING_DOWNLOAD_RESPONSE
                || data.requestId !== requestId) {
                return;
            }

            if (!isOriginAllowed(event.origin)) {
                complete(false);

                return;
            }

            complete(Boolean(data.ok));
        }

        timeoutId = window.setTimeout(() => complete(false), REQUEST_TIMEOUT_MS);

        window.addEventListener('message', onMessage);

        try {
            window.parent.postMessage({
                type: LOCAL_RECORDING_DOWNLOAD_REQUEST,
                payload: {
                    probe: true,
                    requestId
                }
            }, '*');
        } catch (error) {
            logger.warn('Failed to probe parent download bridge availability', error);
            complete(false);
        }
    });
}
